import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import MegaSyncPanel from "@/components/upload/MegaSyncPanel";
import TvSyncPanel from "@/components/upload/TvSyncPanel";
import S4TvManagementPanel from "@/components/upload/S4TvManagementPanel";

const box = "border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] bg-black/25";
const btn = "border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] px-3 py-1.5 text-[9px] tracking-[0.16em] hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)] disabled:opacity-40 disabled:cursor-not-allowed";

function Stat({ label, value, detail, onClick = null, title = "" }) {
  const interactive = typeof onClick === "function";

  const content = (
    <>
      <div className="text-[8px] tracking-[0.18em] opacity-50">{label}</div>
      <div className="text-[22px] leading-none mt-1" style={{ fontFamily: "VT323, monospace" }}>
        {value ?? "—"}
      </div>
      {detail && <div className="text-[8px] opacity-50 mt-1 truncate">{detail}</div>}
    </>
  );

  if (!interactive) {
    return <div className={`${box} p-2.5 min-w-0`}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={title || undefined}
      className={`${box} p-2.5 min-w-0 text-left w-full cursor-pointer transition-all hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_7%,black)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--phosphor)]`}
      data-testid="media-review-summary-entry"
    >
      {content}
      <div className="mt-1.5 text-[7px] tracking-[0.18em] opacity-55">OPEN REVIEW ↓</div>
    </button>
  );
}

export default function MediaManagementPanel({
  apiBase,
  authHeaders,
  channels,
  videosByChannel,
  selectedChannel,
  append,
  onUploaded,
}) {
  const [discovery, setDiscovery] = useState(null);
  const [review, setReview] = useState({ total: 0, retryable: 0, fixable: 0, manual: 0, issues: [] });
  const [activity, setActivity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState("");
  const [showLinkRequest, setShowLinkRequest] = useState(null);
  const [error, setError] = useState("");
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setBusy(true);
    setError("");
    try {
      const [discoveryRes, reviewRes] = await Promise.all([
        axios.get(`${apiBase}/media-ingest/discovery`, { headers: authHeaders() }),
        axios.get(`${apiBase}/media-ingest/review`, { headers: authHeaders() }),
      ]);
      setDiscovery(discoveryRes.data);
      setReview(reviewRes.data || { total: 0, retryable: 0, fixable: 0, manual: 0, issues: [] });
      return discoveryRes.data;
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail);
      append?.(`> MEDIA STATUS ERROR: ${detail}`);
      return null;
    } finally {
      if (!quiet) setBusy(false);
    }
  }, [apiBase, authHeaders, append]);

  useEffect(() => {
    load(false);
  }, [load]);

  const loadActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/media-ingest/status`, {
        headers: authHeaders(),
      });
      setActivity(res.data || null);
    } catch (e) {
      append?.(`> MEDIA ACTIVITY STATUS ERROR: ${e.response?.data?.detail || e.message}`);
    }
  }, [apiBase, authHeaders, append]);

  useEffect(() => {
    loadActivity();
    const timer = window.setInterval(loadActivity, 5000);
    return () => window.clearInterval(timer);
  }, [loadActivity]);

  const runNow = async () => {
    setBusy(true);
    setError("");
    append?.("> AUTOMATIC MEDIA — RUNNING UNIFIED INGEST NOW...");
    try {
      const res = await axios.post(
        `${apiBase}/media-ingest/run`,
        {},
        { headers: authHeaders() }
      );
      setLastRun(res.data);
      const movie = res.data?.movie_summary || {};
      const tv = res.data?.summary || {};
      append?.(
        `> AUTOMATIC MEDIA DONE — TV FAILED ${tv.failed || 0}, MOVIES ADDED ${movie.movies_added || 0}, MOVIE REVIEW ${movie.review || 0}, MOVIE FAILED ${movie.failed || 0}`
      );
      await load(true);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail);
      append?.(`> AUTOMATIC MEDIA ERROR: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const retryIssue = async (issue) => {
    if (!issue?.issue_id || !issue?.retryable) return;
    setRetrying(issue.issue_id);
    setError("");
    append?.(`> MEDIA REVIEW — RETRYING ${issue.media_type?.toUpperCase()} ${issue.title}...`);
    try {
      const res = await axios.post(
        `${apiBase}/media-ingest/review/${encodeURIComponent(issue.issue_id)}/retry`,
        {},
        { headers: authHeaders() }
      );
      append?.(`> MEDIA REVIEW — ${res.data?.resolved ? "RESOLVED" : "RECHECKED"}: ${issue.title}`);
      await load(true);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail);
      append?.(`> MEDIA REVIEW ERROR: ${detail}`);
    } finally {
      setRetrying("");
    }
  };

  const fixTmdbIssue = (issue) => {
    const mappingId = issue?.details?.mapping_id;
    if (!mappingId) {
      const detail = "TMDb repair cannot open because the canonical TV mapping is unavailable.";
      setError(detail);
      append?.(`> MEDIA REVIEW ERROR: ${detail}`);
      return;
    }
    append?.(`> MEDIA REVIEW — OPENING TMDB SHOW LINK REVIEW: ${issue.title}`);
    setShowLinkRequest({
      token: `${issue.issue_id}-${Date.now()}`,
      mapping_id: mappingId,
      query: issue.title || "",
    });
  };

  const reviewAction = (issue) => {
    const type = issue?.action?.type || (issue?.retryable ? "retry" : "manual");
    if (type === "fix_tmdb") return fixTmdbIssue(issue);
    if (type === "retry") return retryIssue(issue);
  };

  const summary = discovery?.summary || {};
  const tvRows = useMemo(() => discovery?.tv || [], [discovery]);
  const movieRows = useMemo(() => discovery?.movies || [], [discovery]);

  const tvAttention = useMemo(
    () => tvRows.filter((row) => row.state !== "KNOWN").length,
    [tvRows]
  );
  const movieAttention = useMemo(
    () => movieRows.filter((row) => row.state !== "KNOWN").length,
    [movieRows]
  );
  const attention = tvAttention + movieAttention;
  const knownCount = useMemo(
    () => (
      tvRows.filter((row) => row.state === "KNOWN").length
      + movieRows.filter((row) => row.state === "KNOWN").length
    ),
    [tvRows, movieRows]
  );

  return (
    <div className="space-y-3" data-testid="media-management-panel">
      <section className={`${box} p-3`} data-testid="production-media-workspace">
        <div className="text-[9px] tracking-[0.22em] opacity-50">PRODUCTION WORKSPACE</div>
        <div className="text-[20px] tracking-[0.12em] mt-1" style={{ fontFamily: "VT323, monospace" }}>MOVIES + TV</div>
        <div className="text-[9px] opacity-60 mt-1 max-w-4xl leading-relaxed">
          Movies and TV use the same operator rhythm: status and review first, then storage management. Matching section treatment makes both sides easier to scan.
        </div>

        <div className="mt-3 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] gap-4 items-start">
          <div className="min-w-0 space-y-3" data-testid="media-movie-column">
            <div className="border-b border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] pb-2">
              <div className="text-[8px] tracking-[0.2em] opacity-50">PRODUCTION STORAGE</div>
              <div className="text-[22px] tracking-[0.14em] mt-1" style={{ fontFamily: "VT323, monospace" }}>MOVIES</div>
              <div className="text-[8px] opacity-55 mt-1">STATUS · REVIEW · ADD · MANAGE · RENAME · MOVE · REPLACE · REMOVE</div>
            </div>

            <MegaSyncPanel
              apiBase={apiBase}
              authHeaders={authHeaders}
              channels={channels}
              selectedChannel={selectedChannel}
              append={append}
              onUploaded={onUploaded}
            />
          </div>

          <div
            aria-hidden="true"
            className="hidden xl:block self-stretch w-px bg-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] min-h-[560px]"
            data-testid="media-workspace-divider"
          />

          <div className="min-w-0 space-y-3 xl:border-l-0 border-t xl:border-t-0 border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] pt-4 xl:pt-0" data-testid="media-tv-column">
            <div className="border-b border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] pb-2">
              <div className="text-[8px] tracking-[0.2em] opacity-50">PRODUCTION STORAGE + HEALTH</div>
              <div className="text-[22px] tracking-[0.14em] mt-1" style={{ fontFamily: "VT323, monospace" }}>TV</div>
              <div className="text-[8px] opacity-55 mt-1">STATUS · REVIEW · MANAGE · RENAME · MOVE · REPLACE · REMOVE · REPAIR</div>
            </div>

            <TvSyncPanel
              apiBase={apiBase}
              authHeaders={authHeaders}
              channels={channels}
              videosByChannel={videosByChannel}
              selectedChannel={selectedChannel}
              append={append}
              onUploaded={onUploaded}
              showLinkRequest={showLinkRequest}
              onShowLinkResolved={async (result) => {
                setShowLinkRequest(null);
                if (result?.review) {
                  setReview(result.review);
                }
                await load(true);
              }}
            />

            <S4TvManagementPanel
              apiBase={apiBase}
              authHeaders={authHeaders}
              append={append}
              onUploaded={onUploaded}
            />
          </div>
        </div>
      </section>
      <section className={`${box} p-3`} data-testid="automatic-media-status">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[9px] tracking-[0.22em] opacity-50">UNIFIED S4 MEDIA INGEST</div>
            <div className="text-[24px] tracking-[0.12em] leading-none mt-1" style={{ fontFamily: "VT323, monospace" }}>
              AUTOMATIC MEDIA
            </div>
            <div className="text-[9px] opacity-60 mt-1 max-w-3xl leading-relaxed">
              Upload TV shows under <b>tv/Show Name/</b> or movies under <b>movies/Cinema Channel/</b>.
              SCARTLEAD.TV discovers, maps, matches TMDb, completes duration metadata and publishes automatically.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] tracking-[0.16em]">● {busy ? "WORKING" : "ACTIVE"}</span>
            <button type="button" onClick={() => load(false)} disabled={busy} className={btn}>REFRESH</button>
            <button type="button" onClick={runNow} disabled={busy} className={btn} data-testid="media-ingest-run-now">RUN NOW</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2 mt-3">
          <Stat label="TV SHOWS" value={summary.tv ?? tvRows.length} detail={`${tvAttention} need attention`} />
          <Stat label="MOVIES" value={summary.movies ?? movieRows.length} detail={`${movieAttention} need attention`} />
          <Stat label="KNOWN" value={knownCount} detail="fully reconciled" />
          <Stat label="NEW" value={summary.NEW ?? 0} detail="awaiting next cycle" />
          <Stat label="NEEDS REPAIR" value={summary.NEEDS_REPAIR ?? 0} detail="automatic repair/review" />
          <Stat
            label="REVIEW REQUIRED"
            value={review.total ?? 0}
            detail={`${review.fixable || 0} fix TMDb · ${review.retryable || 0} retry · ${review.manual || 0} manual`}
            onClick={(review.total || 0) > 0 ? () => {
              const reviewQueue = document.querySelector('[data-testid="media-review-queue"]');
              if (!reviewQueue) return;
              reviewQueue.scrollIntoView({ behavior: "smooth", block: "start" });
              reviewQueue.focus({ preventScroll: true });
            } : null}
            title={(review.total || 0) > 0 ? "Open media requiring review" : ""}
          />
        </div>

        {error && <div className="mt-2 border border-red-500/45 px-2 py-1 text-[9px] text-red-300">{error}</div>}
        {lastRun && <div className="mt-2 text-[8px] opacity-55">LAST MANUAL CYCLE: {lastRun.phase || "UNIFIED"} · MODE {lastRun.mode || "automatic"}</div>}
      </section>

      <section className={`${box} p-3`} data-testid="media-automatic-activity">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[9px] tracking-[0.22em] opacity-50">BACKGROUND AUTOMATION</div>
            <div className="text-[20px] tracking-[0.12em] mt-1" style={{ fontFamily: "VT323, monospace" }}>AUTOMATIC ACTIVITY</div>
            <div className="text-[9px] opacity-60 mt-1">The existing five-minute media worker is shown here; this panel does not change its schedule.</div>
          </div>
          <div className="text-[9px] tracking-[0.16em]">● {activity?.state === "running" ? "RUNNING" : "ACTIVE"}</div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <Stat label="LAST CYCLE"
            value={activity?.last_completed_at ? new Date(activity.last_completed_at).toLocaleTimeString() : "—"}
            detail={activity?.last_duration_ms != null ? `${(activity.last_duration_ms / 1000).toFixed(1)}s duration` : "waiting for first cycle"} />
          <Stat label="NEXT CHECK"
            value={activity?.state === "running" ? "NOW" : (activity?.next_run_at ? `${Math.max(0, Math.ceil((new Date(activity.next_run_at).getTime() - Date.now()) / 60000))}m` : "—")}
            detail={`${activity?.interval_seconds || 300}s cadence`} />
          <Stat label="REVIEW" value={activity?.last_summary?.review_required ?? review.total ?? 0} detail="last completed cycle" />
          <Stat label="FAILED" value={activity?.last_summary?.failed ?? 0} detail={activity?.last_error ? "last cycle error" : "last completed cycle"} />
        </div>

        {activity?.last_summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2 text-[9px]">
            <div>TV CHECKED <b>{activity.last_summary.tv_checked ?? 0}</b></div>
            <div>EPISODES ADDED <b>{activity.last_summary.episodes_added ?? 0}</b></div>
            <div>MOVIE FOLDERS <b>{activity.last_summary.movie_folders_checked ?? 0}</b></div>
            <div>MOVIES ADDED <b>{activity.last_summary.movies_added ?? 0}</b></div>
            <div>REPAIRS <b>{activity.last_summary.repairs ?? 0}</b></div>
          </div>
        )}

        {activity?.last_error && (
          <div className="mt-2 border border-red-500/45 px-2 py-1 text-[9px] text-red-300">LAST ERROR: {activity.last_error}</div>
        )}

        <div className="mt-3 border-t border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] pt-2">
          <div className="text-[8px] tracking-[0.18em] opacity-50">RECENT ACTIVITY</div>
          {(activity?.recent_events || []).length ? (
            <div className="mt-1 space-y-1">
              {(activity.recent_events || []).slice(0, 6).map((event, index) => (
                <div key={`${event.at}-${index}`} className="text-[9px] opacity-70">
                  {event.at ? new Date(event.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} · {event.message}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-1 text-[9px] opacity-45">No completed activity recorded since the service started.</div>
          )}
        </div>
      </section>

      <section
        className={`${box} p-3 scroll-mt-3`}
        data-testid="media-review-queue"
        tabIndex={-1}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[9px] tracking-[0.22em] opacity-50">EXCEPTIONS ONLY</div>
            <div className="text-[20px] tracking-[0.12em] mt-1" style={{ fontFamily: "VT323, monospace" }}>REVIEW REQUIRED</div>
            <div className="text-[9px] opacity-60 mt-1">Normal uploads need no action here. Only media SCARTLEAD.TV could not safely finish appears below.</div>
          </div>
          <div className="text-[9px] tracking-[0.14em] opacity-65">{review.total || 0} OPEN</div>
        </div>
        {(review.issues || []).length === 0 ? (
          <div className="mt-3 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-3" data-testid="media-review-clear">
            <div className="text-[16px] tracking-[0.14em]" style={{ fontFamily: "VT323, monospace" }}>ALL SYSTEMS OK</div>
            <div className="text-[9px] opacity-55 mt-1">No media currently requires intervention.</div>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {(review.issues || []).map((issue) => (
              <div key={issue.issue_id} className="border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[8px] tracking-[0.18em] opacity-50">{String(issue.media_type || "").toUpperCase()} · {issue.category}</div>
                    <div className="text-[15px] tracking-[0.08em] mt-0.5 truncate" style={{ fontFamily: "VT323, monospace" }}>{issue.title}</div>
                    <div className="text-[9px] opacity-65 mt-1 leading-relaxed">{issue.reason}</div>
                  </div>
                  <div className="shrink-0">
                    {(issue.action?.type === "fix_tmdb" || issue.action?.type === "retry" || issue.retryable) ? (
                      <button type="button" className={btn} disabled={busy || Boolean(retrying)} onClick={() => reviewAction(issue)}>
                        {retrying === issue.issue_id ? "RETRYING…" : (issue.action?.label || "RETRY")}
                      </button>
                    ) : (
                      <span className="text-[8px] tracking-[0.14em] opacity-50">{issue.action?.label || "MANUAL REVIEW"}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

    </div>
  );
}
